"use client";

import { useRef, useState, type ReactNode } from "react";
import { Zustandspille } from "../zustandspille";
import { feldIds, useFeldId } from "./feld";
import { Feldhilfe, type FeldhilfeInhalt } from "./feldhilfe";

/**
 * Optionsgruppe (Radio, M-002 F10): wenige, sich ausschließende Optionen. Mehr als etwa fünf Optionen gehören in
 * eine Auswahl (F6). Als `radiogroup` mit benannter Legende gebaut, damit Hilfstechnik die Gruppe benennt. Der Wert ist
 * unkontrolliert (`vorgabe`) oder kontrolliert (`wert` + `beiAenderung`). Pflicht bleibt unmarkiert (F5).
 */
export interface OptionsgruppeOption {
  wert: string;
  label: string;
  hilfe?: string;
  /** Nur Wahlkarten (`karten`): Beispielzeile unter der Hilfe, klein und gedämpft („Beispiel: jedes Brötchen 2,50 €“). Additiv, Preismodell 20.10.2026. */
  beispiel?: string;
  deaktiviert?: boolean;
  /** Symbol vor dem Text (nur Segmentleiste), `aria-hidden`; der Aufrufer liefert Lucide. */
  symbol?: ReactNode;
  /**
   * Nur Segmentleiste: Tönung der GEWÄHLTEN Option (Betreiber 17.10.2026, Vorschau 3A, Abweichung AW-111): `erfolg` grün, `risiko` rot,
   * `gedaempft` neutral. Ohne Angabe bleibt die gewählte Option wie bisher weiß abgesetzt. Die Farbe trägt nie allein die Aussage (das Wort steht im Segment).
   */
  tonung?: "erfolg" | "warnung" | "risiko" | "gedaempft";
}

/** Gewählte Option der Segmentleiste: ohne Tönung weiß abgesetzt (AW-012), mit Tönung die Fläche des Tons (Abweichung AW-111; nur Theme-Tokens). */
const SEGMENT_GEWAEHLT = "has-checked:bg-layer has-checked:shadow-2xs";
const SEGMENT_GETOENT: Record<NonNullable<OptionsgruppeOption["tonung"]>, string> = {
  erfolg: "has-checked:bg-status-ok/10 has-checked:text-status-ok-text has-checked:font-medium has-checked:shadow-2xs",
  // Einkauf E1 (Rundgang 1A, „Wenig“/„Angebrochen“): Warnton, Erweiterung von AW-111.
  warnung: "has-checked:bg-status-warn/10 has-checked:text-status-warn-text has-checked:font-medium has-checked:shadow-2xs",
  risiko: "has-checked:bg-destructive/10 has-checked:text-destructive has-checked:font-medium has-checked:shadow-2xs",
  gedaempft: "has-checked:bg-muted has-checked:text-muted-foreground-1 has-checked:font-medium has-checked:shadow-2xs",
};

/**
 * Größe `touch` der Segmentleiste (Einkauf E1, Rundgang 1A, Betreiber 20.10.2026 „Empfehlung“; additiv wie `Knopf` `touch`, AW-120): einzelne
 * Wahlknöpfe mit Rahmen, 44 px hoch in JEDER Breite (Daumen am Handy), unter sm volle Breite und gleich breit. Gewählt: Rahmen und Fläche im Ton der
 * Option (Tönung AW-111), ohne Tönung im Primärton. Wort und Symbol stehen immer im Knopf, die Farbe trägt nie allein die Aussage.
 */
const TOUCH_GEWAEHLT = "has-checked:ring-2 has-checked:ring-primary-checked has-checked:font-medium";
const TOUCH_GETOENT: Record<NonNullable<OptionsgruppeOption["tonung"]>, string> = {
  erfolg: "has-checked:ring-2 has-checked:ring-status-ok has-checked:bg-status-ok/10 has-checked:text-status-ok-text has-checked:font-medium",
  warnung: "has-checked:ring-2 has-checked:ring-status-warn has-checked:bg-status-warn/10 has-checked:text-status-warn-text has-checked:font-medium",
  risiko: "has-checked:ring-2 has-checked:ring-destructive has-checked:bg-destructive/10 has-checked:text-destructive has-checked:font-medium",
  gedaempft: "has-checked:ring-2 has-checked:ring-line-3 has-checked:bg-muted has-checked:text-muted-foreground-1 has-checked:font-medium",
};

const RADIO =
  "shrink-0 mt-0.5 size-4 bg-transparent border-line-3 rounded-full shadow-2xs text-primary focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-primary-focus checked:bg-primary-checked checked:border-primary-checked disabled:opacity-50 disabled:pointer-events-none";
const RADIO_FEHLER =
  "shrink-0 mt-0.5 size-4 bg-transparent border-red-500 rounded-full shadow-2xs text-primary focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-primary-focus checked:bg-primary-checked checked:border-primary-checked disabled:opacity-50 disabled:pointer-events-none";

export function Optionsgruppe({
  id: idVorgabe,
  legende,
  name,
  optionen,
  vorgabe,
  wert,
  beiAenderung,
  anordnung = "vertikal",
  variante = "liste",
  legendeVersteckt = false,
  optional = false,
  hilfe,
  feldhilfe,
  fehler,
  deaktiviert = false,
  groesse = "standard",
  spalten = 1,
}: {
  id?: string;
  /**
   * Nur Wahlkarten (`karten`): `1` = einspaltig (Standard, AW-057), `3` = ab `lg` drei Karten nebeneinander wie der Katalogblock grid-radio-cards
   * (Preismodell 20.10.2026: drei gleichrangige Modelle mit Beispielzeile); darunter einspaltig.
   */
  spalten?: 1 | 3;
  /** Nur Segmentleiste: `touch` = einzelne Wahlknöpfe, 44 px hoch in jeder Breite (Einkauf E1, Rundgang). */
  groesse?: "standard" | "touch";
  legende: string;
  name: string;
  optionen: OptionsgruppeOption[];
  /** Vorgewählter Wert (unkontrolliert). */
  vorgabe?: string;
  /** Gewählter Wert (kontrolliert, zusammen mit `beiAenderung`). */
  wert?: string;
  beiAenderung?: (wert: string) => void;
  anordnung?: "vertikal" | "inline";
  /**
   * `segment`: Segmentleiste (Fieldset/Legende, Pfeiltasten, Symbol je Option), Betreiber 08.10.2026 (2F). `bildleiste` (20.10.2026):
   * kleine Wahlkacheln mit Bild (Symbol) über einer kurzen Beschriftung, umbrechend; Pfeiltasten und Legende wie die Segmentleiste.
   */
  variante?: "liste" | "segment" | "karten" | "bildleiste";
  /** Nur Segmentleiste: Legende nur für Hilfstechnik (`sr-only`), sichtbar bleibt die Leiste (Besprechungen P1, A6 N1). */
  legendeVersteckt?: boolean;
  optional?: boolean;
  hilfe?: string;
  /** Feldhilfe (M-060): „?“ an der sichtbaren Legende; nur Katalogeintrag des Moduls. Entfällt bei `legendeVersteckt`. */
  feldhilfe?: FeldhilfeInhalt;
  fehler?: string;
  deaktiviert?: boolean;
}) {
  const id = useFeldId(idVorgabe);
  const ids = feldIds(id);
  const legendeId = `${id}-legende`;
  // Legende mit „?“: der Gruppenname bleibt nur der Legendentext (aria-labelledby), nicht der Name des Hilfe-Knopfes.
  const mitHilfe = Boolean(feldhilfe);
  const legendeInhalt = feldhilfe ? (
    <span className="inline-flex items-center gap-x-1.5">
      <span id={legendeId}>{legende}</span>
      <Feldhilfe label={legende} inhalt={feldhilfe} />
    </span>
  ) : (
    legende
  );
  const [intern, setIntern] = useState(vorgabe ?? "");
  const kontrolliert = wert !== undefined;
  const gewaehlt = kontrolliert ? wert : intern;
  const beschreibung = [hilfe ? ids.hilfe : null, fehler ? ids.fehler : null].filter(Boolean).join(" ") || undefined;
  const felder = useRef<Record<string, HTMLInputElement | null>>({});
  const waehle = (neu: string) => {
    if (!kontrolliert) setIntern(neu);
    beiAenderung?.(neu);
  };
  // Pfeiltasten wechseln die Option und setzen den Fokus mit (Radio-Gruppe, auch wo der Browser es nicht selbst tut).
  const beiTaste = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    const vor = e.key === "ArrowRight" || e.key === "ArrowDown";
    const zurueck = e.key === "ArrowLeft" || e.key === "ArrowUp";
    if (!vor && !zurueck) return;
    e.preventDefault();
    const frei = optionen.filter((o) => !(deaktiviert || o.deaktiviert));
    const aktuell = frei.findIndex((o) => o.wert === optionen[index]!.wert);
    const ziel = frei[(aktuell + (vor ? 1 : -1) + frei.length) % frei.length];
    if (!ziel) return;
    felder.current[ziel.wert]?.focus();
    waehle(ziel.wert);
  };
  if (variante === "karten") {
    // Wahlkarten (Entscheidungen E-c, Betreiber 13.10.2026, E4 B „Radio-Karten“): jede Option eine Karte mit Radio, Titel und Zusatzzeile.
    return (
      <>
        {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-selectable-cards-and-lists/grid-radio-cards (Datei: bloecke/forms/forms-cat-selectable-cards-and-lists/grid-radio-cards.html, die Unterbereichsebene fehlt dort; einspaltig, mit Legende, Hilfe und Feldfehler) */}
        {/* Abweichung AW-057: Wahlkarten einspaltig, mit Fieldset und Legende, Fokusring und gesperrtem Zustand ergänzt; Feldfehler als Pille (AW-005) */}
        <fieldset aria-labelledby={mitHilfe ? legendeId : undefined} aria-describedby={beschreibung} aria-invalid={fehler ? true : undefined} className="min-w-0">
          <legend className="mb-2 block text-sm font-medium text-foreground">{legendeInhalt}</legend>
          <div className={spalten === 3 ? "grid gap-3 lg:grid-cols-3" : "grid gap-3"}>
            {optionen.map((o) => {
              const optionId = `${id}-${o.wert}`;
              return (
                <label
                  key={o.wert}
                  htmlFor={optionId}
                  className="py-3 px-4 flex text-sm bg-layer text-layer-foreground rounded-xl cursor-pointer ring-1 ring-layer-line has-checked:ring-2 has-checked:ring-primary-checked has-focus-visible:ring-2 has-focus-visible:ring-primary-focus has-disabled:opacity-50 has-disabled:cursor-default"
                >
                  <input
                    id={optionId}
                    type="radio"
                    name={name}
                    value={o.wert}
                    className="shrink-0 mt-0.5 size-4 bg-transparent border-layer-line text-primary rounded-full focus:ring-0 focus:ring-offset-0 checked:bg-primary-checked checked:border-primary-checked"
                    checked={gewaehlt === o.wert}
                    disabled={deaktiviert || o.deaktiviert}
                    onChange={() => waehle(o.wert)}
                  />
                  <span className="grow ps-3 min-w-0">
                    <span className="block font-medium">{o.label}</span>
                    {o.hilfe && <span className="block text-sm/6 text-muted-foreground-1">{o.hilfe}</span>}
                    {o.beispiel && <span className="mt-2 block text-xs text-muted-foreground-1">{o.beispiel}</span>}
                  </span>
                </label>
              );
            })}
          </div>
          {hilfe && (
            <p id={ids.hilfe} className="mt-2 text-sm text-muted-foreground-1">
              {hilfe}
            </p>
          )}
          {fehler && (
            <p id={ids.fehler} className="mt-2">
              {/* Abweichung AW-005: Feldfehler V2, Satz in Pille */}
              <Zustandspille ton="gefahr" mitPunkt>{fehler}</Zustandspille>
            </p>
          )}
        </fieldset>
      </>
    );
  }
  if (variante === "bildleiste") {
    return (
      <>
        {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-selectable-cards-and-lists/grid-radio-cards (Datei: bloecke/forms/forms-cat-selectable-cards-and-lists/grid-radio-cards.html) */}
        {/* Abweichung AW-126 (1): Wahlkarten als kleine Bildkacheln (Skizze über Kurzname), umbrechend, Radio verborgen wie die Segmentleiste (AW-012); Pfeiltasten und Roving Tabindex */}
        <fieldset aria-labelledby={mitHilfe && !legendeVersteckt ? legendeId : undefined} aria-describedby={beschreibung} aria-invalid={fehler ? true : undefined} className="min-w-0">
          <legend className={legendeVersteckt ? "sr-only" : "mb-2 block text-sm font-medium text-foreground"}>{legendeVersteckt ? legende : legendeInhalt}</legend>
          <div className="flex flex-wrap gap-2">
            {optionen.map((o) => {
              const optionId = `${id}-${o.wert}`;
              const gesperrt = deaktiviert || o.deaktiviert;
              return (
                <label
                  key={o.wert}
                  htmlFor={optionId}
                  title={o.hilfe}
                  className="relative min-w-16 py-1.5 px-2 inline-flex flex-col items-center gap-y-1 cursor-pointer text-center text-xs text-muted-foreground-1 bg-layer rounded-lg ring-1 ring-layer-line hover:bg-muted-hover has-checked:ring-2 has-checked:ring-primary-checked has-checked:text-primary has-checked:font-medium has-focus-visible:ring-2 has-focus-visible:ring-primary-focus has-disabled:opacity-50 has-disabled:cursor-default"
                >
                  <input
                    ref={(el) => {
                      felder.current[o.wert] = el;
                    }}
                    id={optionId}
                    type="radio"
                    name={name}
                    value={o.wert}
                    className="sr-only"
                    checked={gewaehlt === o.wert}
                    disabled={gesperrt}
                    aria-label={o.hilfe ? `${o.label}: ${o.hilfe}` : undefined}
                    tabIndex={gewaehlt === o.wert || (!gewaehlt && o === optionen[0]) ? 0 : -1}
                    onChange={() => waehle(o.wert)}
                    onKeyDown={(e) => beiTaste(e, optionen.indexOf(o))}
                  />
                  {o.symbol && (
                    <span className="shrink-0 inline-flex" aria-hidden="true">
                      {o.symbol}
                    </span>
                  )}
                  <span className="whitespace-nowrap">{o.label}</span>
                </label>
              );
            })}
          </div>
          {hilfe && (
            <p id={ids.hilfe} className="mt-2 text-sm text-muted-foreground-1">
              {hilfe}
            </p>
          )}
          {fehler && (
            <p id={ids.fehler} className="mt-2">
              {/* Abweichung AW-005: Feldfehler V2, Satz in Pille */}
              <Zustandspille ton="gefahr" mitPunkt>{fehler}</Zustandspille>
            </p>
          )}
        </fieldset>
      </>
    );
  }
  if (variante === "segment" && groesse === "touch") {
    return (
      <>
        {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-option-pickers/split-selector-with-options (Datei: bloecke/forms/forms-cat-option-pickers/split-selector-with-options.html) */}
        {/* Abweichung AW-012: Segmentleiste mit angepasstem Markup (Tastatur, Barrierefreiheit); Größe touch als einzelne Wahlknöpfe (AW-127 (2)), Tönung AW-111 */}
        <fieldset aria-labelledby={mitHilfe && !legendeVersteckt ? legendeId : undefined} aria-describedby={beschreibung} aria-invalid={fehler ? true : undefined} className="min-w-0">
          <legend className={legendeVersteckt ? "sr-only" : "mb-2 block text-sm font-medium text-foreground"}>{legendeVersteckt ? legende : legendeInhalt}</legend>
          <div className="flex w-full sm:inline-flex sm:w-auto items-stretch gap-2">
            {optionen.map((o) => {
              const optionId = `${id}-${o.wert}`;
              const gesperrt = deaktiviert || o.deaktiviert;
              return (
                <label
                  key={o.wert}
                  htmlFor={optionId}
                  className={[
                    "relative flex-1 sm:flex-none min-w-0 py-2 px-3 min-h-11 inline-flex items-center justify-center gap-x-2 cursor-pointer text-center text-sm text-foreground bg-layer rounded-lg ring-1 ring-layer-line hover:bg-muted-hover",
                    o.tonung ? TOUCH_GETOENT[o.tonung] : TOUCH_GEWAEHLT,
                    "has-focus-visible:ring-2 has-focus-visible:ring-primary-focus has-disabled:opacity-50 has-disabled:cursor-default",
                  ].join(" ")}
                >
                  <input
                    ref={(el) => {
                      felder.current[o.wert] = el;
                    }}
                    id={optionId}
                    type="radio"
                    name={name}
                    value={o.wert}
                    className="sr-only"
                    checked={gewaehlt === o.wert}
                    disabled={gesperrt}
                    tabIndex={gewaehlt === o.wert || (!gewaehlt && o === optionen[0]) ? 0 : -1}
                    onChange={() => waehle(o.wert)}
                    onKeyDown={(e) => beiTaste(e, optionen.indexOf(o))}
                  />
                  {o.symbol && (
                    <span className="shrink-0 inline-flex" aria-hidden="true">
                      {o.symbol}
                    </span>
                  )}
                  {o.label}
                </label>
              );
            })}
          </div>
          {hilfe && (
            <p id={ids.hilfe} className="mt-2 text-sm text-muted-foreground-1">
              {hilfe}
            </p>
          )}
          {fehler && (
            <p id={ids.fehler} className="mt-2">
              {/* Abweichung AW-005: Feldfehler V2, Satz in Pille */}
              <Zustandspille ton="gefahr" mitPunkt>{fehler}</Zustandspille>
            </p>
          )}
        </fieldset>
      </>
    );
  }
  if (variante === "segment") {
    return (
      <>
        {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-option-pickers/split-selector-with-options (Datei: bloecke/forms/forms-cat-option-pickers/split-selector-with-options.html; der Katalogpfad führt die Unterbereichsebene forms-sub-core-inputs-layouts, die Datei liegt ohne sie) */}
        {/* Abweichung AW-012: Segmentleiste (nur die Leiste als Herkunft), angepasstes Markup für Tastatur und Barrierefreiheit */}
        <fieldset aria-labelledby={mitHilfe && !legendeVersteckt ? legendeId : undefined} aria-describedby={beschreibung} aria-invalid={fehler ? true : undefined} className="min-w-0">
          <legend className={legendeVersteckt ? "sr-only" : "mb-2 block text-sm font-medium text-foreground"}>{legendeVersteckt ? legende : legendeInhalt}</legend>
          {/* Abweichung AW-012: unter sm volle Breite mit gleich breiten Segmenten, ab sm inhaltsbreit */}
          <div className="flex w-full sm:inline-flex sm:w-auto items-center bg-surface rounded-xl p-1">
            {optionen.map((o) => {
              const optionId = `${id}-${o.wert}`;
              const gesperrt = deaktiviert || o.deaktiviert;
              return (
                <label
                  key={o.wert}
                  htmlFor={optionId}
                  className={[
                    "relative flex-1 sm:flex-none min-w-0 py-1.5 px-2 sm:px-3 min-h-9 inline-flex items-center justify-center gap-x-1.5 cursor-pointer text-center text-sm text-foreground rounded-lg",
                    o.tonung ? SEGMENT_GETOENT[o.tonung] : SEGMENT_GEWAEHLT,
                    "has-focus-visible:ring-2 has-focus-visible:ring-primary-focus has-disabled:opacity-50 has-disabled:cursor-default",
                  ].join(" ")}
                >
                  <input
                    ref={(el) => {
                      felder.current[o.wert] = el;
                    }}
                    id={optionId}
                    type="radio"
                    name={name}
                    value={o.wert}
                    className="sr-only"
                    checked={gewaehlt === o.wert}
                    disabled={gesperrt}
                    // Roving Tabindex: nur die gewählte Option ist per Tab erreichbar, die übrigen per Pfeiltaste.
                    tabIndex={gewaehlt === o.wert || (!gewaehlt && o === optionen[0]) ? 0 : -1}
                    onChange={() => waehle(o.wert)}
                    onKeyDown={(e) => beiTaste(e, optionen.indexOf(o))}
                  />
                  {o.symbol && (
                    <span className="shrink-0 inline-flex" aria-hidden="true">
                      {o.symbol}
                    </span>
                  )}
                  {o.label}
                </label>
              );
            })}
          </div>
          {hilfe && (
            <p id={ids.hilfe} className="mt-2 text-sm text-muted-foreground-1">
              {hilfe}
            </p>
          )}
          {fehler && (
            <p id={ids.fehler} className="mt-2">
              {/* Abweichung AW-005: Feldfehler V2, Satz in Pille */}
              <Zustandspille ton="gefahr" mitPunkt>{fehler}</Zustandspille>
            </p>
          )}
        </fieldset>
      </>
    );
  }
  return (
    <>
      {/* Preline: component/radio/radio-vertical-radio-group */}
      <div role="radiogroup" aria-labelledby={`${id}-legende`} aria-describedby={beschreibung} aria-invalid={fehler ? true : undefined}>
        <div className="mb-2 flex flex-wrap justify-between items-center gap-2">
          {feldhilfe ? (
            <span className="block text-sm font-medium text-foreground">{legendeInhalt}</span>
          ) : (
            <span id={legendeId} className="block text-sm font-medium text-foreground">
              {legende}
            </span>
          )}
          {optional && <span className="block text-sm text-muted-foreground-1">Optional</span>}
        </div>
        <div className={anordnung === "inline" ? "flex flex-wrap gap-x-6 gap-y-3" : "flex flex-col gap-y-3"}>
          {optionen.map((o) => {
            const optionId = `${id}-${o.wert}`;
            return (
              <div key={o.wert} className="flex gap-x-3">
                <input
                  id={optionId}
                  type="radio"
                  name={name}
                  value={o.wert}
                  checked={gewaehlt === o.wert}
                  disabled={deaktiviert || o.deaktiviert}
                  className={fehler ? RADIO_FEHLER : RADIO}
                  onChange={() => {
                    if (!kontrolliert) setIntern(o.wert);
                    beiAenderung?.(o.wert);
                  }}
                />
                <div>
                  <label htmlFor={optionId} className="text-sm text-foreground">
                    {o.label}
                  </label>
                  {o.hilfe && <p className="text-sm text-muted-foreground-1">{o.hilfe}</p>}
                </div>
              </div>
            );
          })}
        </div>
        {hilfe && (
          <p id={ids.hilfe} className="mt-2 text-sm text-muted-foreground-1">
            {hilfe}
          </p>
        )}
        {fehler && (
          <p id={ids.fehler} className="mt-2">
            {/* Abweichung AW-005: Feldfehler V2, Satz in Pille */}
            <Zustandspille ton="gefahr" mitPunkt>{fehler}</Zustandspille>
          </p>
        )}
      </div>
    </>
  );
}
